import { useState } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate API_URL is defined
    if (!API_URL) {
      toast.error("Configuration error. Please contact support at (540) 423-5754");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      toast.success("Thank you! We'll contact you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        projectType: "",
        message: ""
      });
    } catch (error) {
      // More specific error message for network issues
      if (error.message.includes('fetch') || error.name === 'TypeError') {
        toast.error("Unable to connect. Please check your internet connection or call us at (540) 423-5754");
      } else {
        toast.error("Something went wrong. Please try again or call us at (540) 423-5754");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="pt-32 pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your <span className="text-crimson-600">Free Quote</span>
          </h2>
          <p className="text-lg text-gray-600">
            Ready to transform your home? Contact us today for a free consultation and quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-6">
                <Card className="border-2 hover:border-crimson-600 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-crimson-100 rounded-lg flex items-center justify-center">
                          <Phone className="w-6 h-6 text-crimson-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                        <a href="tel:5404235754" className="text-gray-600 hover:text-crimson-600 transition-colors">
                          (540) 423-5754
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-crimson-600 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-crimson-100 rounded-lg flex items-center justify-center">
                          <Mail className="w-6 h-6 text-crimson-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Email</h4>
                        <a href="mailto:hernandezremodeling81@gmail.com" className="text-gray-600 hover:text-crimson-600 transition-colors break-all">
                          hernandezremodeling81@gmail.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-crimson-600 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-crimson-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-crimson-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Service Area</h4>
                        <p className="text-gray-600">
                          Gordonsville, Charlottesville, Louisa, Culpeper, Orange, and Surrounding Areas
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-crimson-600 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-crimson-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-6 h-6 text-crimson-600" />
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1">Business Hours</h4>
                        <p className="text-gray-600">
                          Monday - Friday: 8:00 AM - 5:00 PM
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-crimson-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Free, no-obligation quotes</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Family-owned and operated</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Transparent pricing and timelines</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Quality materials and craftsmanship</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>10+ years of experience</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Quote Form */}
          <div>
            <Card className="border-2">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Request a Free Quote</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(540) 555-0123"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      required
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson-600 focus:border-transparent"
                    >
                      <option value="">Select a service</option>
                      <option value="kitchen">Kitchen Remodeling</option>
                      <option value="bathroom">Bathroom Renovation</option>
                      <option value="basement">Basement Finishing</option>
                      <option value="flooring">Flooring Installation</option>
                      <option value="roofing">Roofing Services</option>
                      <option value="painting">Painting & Finishing</option>
                      <option value="drywall">Drywall & Sheetrock</option>
                      <option value="deck">Deck Construction</option>
                      <option value="addition">Home Addition</option>
                      <option value="plumbing">Plumbing Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Project Details *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={5}
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-crimson-600 hover:bg-crimson-700 text-white font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? "Sending..." : "Get Free Quote"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
